import type Component from '@highcharts/dashboards/es-modules/Dashboards/Components/Component.js';
import { ProviderConnector } from '../data/provider-connector';

/** Why a connector ended up in `error`. */
export type DashboardDataErrorReason = 'missing-provider' | 'load-failed';

export type DashboardDataState =
    /** The cell's connectors are being resolved. */
    | { readonly status: 'loading' }
    /** Data arrived, or the cell is not data-bound and draws itself. */
    | { readonly status: 'ready' }
    /** Data arrived but carries no rows. */
    | { readonly status: 'empty' }
    /** The data could not be read. */
    | { readonly status: 'error'; readonly reason: DashboardDataErrorReason };

/** Reads a component's data state off the connectors bound to it. */
export function dataStateOf(component: Component): DashboardDataState {
    const bound = component.connectorHandlers.filter((handler) => !!handler.options.id);
    if (bound.length === 0) {
        // Not data-bound
        return { status: 'ready' };
    }

    let loading = false;
    for (const handler of bound) {
        const connector = handler.connector;
        if (!connector) {
            // Nothing resolved from the data pool for this handler yet.
            loading = true;
            continue;
        }
        if (!(connector instanceof ProviderConnector)) {
            continue;
        }
        if (connector.state.status === 'error') {
            // The widget has nothing whole to draw.
            return connector.state;
        }
        loading ||= connector.state.status === 'loading';
    }

    if (loading) {
        return { status: 'loading' };
    }

    const hasRows = bound.some((handler) => (handler.dataTable?.rowCount ?? 0) > 0);
    return { status: hasRows ? 'ready' : 'empty' };
}
